<template>
    <div id="login_main" class="organisation-select">
        <div id="login_container">
            <div class="space-less"></div>
            <div>
                <div id="login_welcome">
                    <div id="organisation_icon"><div></div></div>
                    <div id="login_welcome_message">{{ selected.name }}</div>
                </div>
                <div id="login_content">
                    <ul id="login_credentials">
                        <li>
                            <label class="login-label">{{ $t('Search Organisation') }}</label>
                            <cic-textbox
                                id="prefix_search"
                                :value="searchText"
                                @keyup.enter="handleSearch"
                                @input="(val) => (searchText = val)"
                            />
                        </li>
                        <li>
                            <label class="login-label organisation-select-label">
                                {{ $t('Select an Organisation') }}
                            </label>
                            <SearchTree
                                v-if="lastSearch !== '' && organisations"
                                :data="organisations"
                                :selected="selected.id"
                                @click="handleClick"
                            />
                            <LazyTree
                                v-else
                                :org-id="ownerId"
                                :org-name="ownerName"
                                @change="(val) => $store.setUser({ ...$store.user, rights: val })"
                                @click="handleClick"
                            />
                        </li>
                        <template v-if="canImpersonate">
                            <li>
                                <label class="login-label">{{ $t('Impersonate User') }}</label>
                                <CicSelect
                                    :value="impersonateUserId"
                                    :options="
                                        users.map((user) => ({
                                            value: user.userId,
                                            label: `${user.firstName} ${user.lastName} (${user.username})`,
                                        }))
                                    "
                                    :placeholder="$t('No User Selected')"
                                    clearable
                                    @input="(val) => (impersonateUserId = val)"
                                />
                                <label class="login-explain">{{ $t('Please select a user to login as') }}</label>
                            </li>
                        </template>
                    </ul>

                    <div class="cic-control-bar">
                        <cic-button id="loginButton" @click="login">
                            {{ $t('Continue') }}
                        </cic-button>
                    </div>
                </div>
            </div>
            <div class="space-more"></div>
        </div>
    </div>
</template>

<script>
    import CicButton from '@/components/CicButton';
    import CicSelect from '@/components/inputs/CicSelect';
    import CicTextbox from '@/components/inputs/CicTextbox';
    import { load, redirect } from '@/utils/Http';
    import LazyTree from './LazyTree';
    import SearchTree from './SearchTree';
    import './resources/styles/suborganisationLogin.scss';

    export default {
        name: 'SuborganisationLogin',
        components: {
            CicSelect,
            CicButton,
            CicTextbox,
            LazyTree,
            SearchTree,
        },
        data() {
            return {
                lastSearch: '',
                impersonateUserId: null,
                organisations: null,
                searchText: '',
                selected: null,
                users: [],
                userId: null,
            };
        },
        computed: {
            ownerId() {
                return this.$store.user.impersonator ? this.$store.user.impersonator.ownerId : this.$store.user.ownerId;
            },
            ownerName() {
                return this.$store.user.impersonator
                    ? this.$store.user.impersonator.ownerName
                    : this.$store.user.ownerName;
            },
            canImpersonate() {
                return (
                    (this.$store.user.impersonator || this.$store.hasRight('IMPERSONATE_USER')) &&
                    this.selected.id !== this.ownerId
                );
            },
        },
        created() {
            this.selected = { id: this.$store.user.ownerId, name: this.$store.user.ownerName };
            if (this.$store.user.impersonator) {
                this.searchText = this.$store.user.ownerName;
                this.handleSearch();
            }
        },
        methods: {
            findMatch(branch) {
                if (branch.name.toUpperCase().substring(0, this.searchText.length) === this.searchText.toUpperCase()) {
                    return { id: branch.id, name: branch.name };
                } else if (!branch.leaf) {
                    const match = branch.children.find((x) => this.findMatch(x));
                    if (match) {
                        return this.findMatch(match);
                    }
                }

                return null;
            },
            handleClick(val) {
                if (this.selected.id !== val.id) {
                    this.selected = val;
                    this.impersonateUserId = null;
                    if (this.canImpersonate) {
                        load('organisation/portalusers', { orgId: val.id }, (response) => {
                            this.users = response.users;
                            if (this.$store.user.impersonator && this.$store.user.ownerId === val.id) {
                                this.impersonateUserId = this.$store.user.userId;
                            }
                        });
                    }
                }
            },
            handleSearch() {
                if (this.searchText !== this.lastSearch) {
                    if (this.searchText && this.searchText.split('').some((x) => new Blob([x]).size >= 3)) {
                        alert(this.$t('3 and 4 byte characters are not supported'));
                        return;
                    }

                    this.selected = { id: this.ownerId, name: this.ownerName };
                    this.impersonateUserId = null;
                    if (this.searchText !== '') {
                        this.organisations = null;
                        load(
                            'organisation/tree',
                            {
                                orgId: this.ownerId,
                                searchText: this.searchText,
                                expandAll: 1,
                            },
                            (response) => {
                                this.$store.setUser({ ...this.$store.user, rights: response.rights });
                                if (response.organisations.length > 0) {
                                    this.organisations = [
                                        {
                                            name: this.ownerName,
                                            id: this.ownerId,
                                            leaf: false,
                                            children: response.organisations,
                                        },
                                    ];
                                    const match = this.findMatch(this.organisations[0]);
                                    if (match) {
                                        this.handleClick(match);
                                    }
                                }
                            },
                        );
                    }
                    this.lastSearch = this.searchText;
                }
            },
            login() {
                load('organisation/login', { orgId: this.selected.id, userId: this.impersonateUserId }, () => {
                    redirect('menu?lang=' + this.$store.requestParams.lang);
                });
            },
        },
    };
</script>
